import React, { useCallback, useEffect, useState } from 'react';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import SearchableSelect from '../common/SearchableSelect';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { ProvidentFundService } from '../../services/providentFund.service';
import { IPfLedgerEntry, IPfMember, IPfMemberLedger, PfMemberStatus } from '../../interfaces/providentFund.interface';
import { useToast } from '../../context/ToastContext';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { humanizeEnum } from '../../utils/format';
import { money, periodLabel } from './provisionShared';

interface Props {
    institutionId: string;
    canManage: boolean;
}

const STATUSES: { id: PfMemberStatus; label: string }[] = [
    { id: 'ACTIVE', label: 'Active' },
    { id: 'LEFT', label: 'Left' },
];

const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));

export const LedgerTable: React.FC<{ entries: IPfLedgerEntry[]; pageKey: string }> = ({ entries, pageKey }) => {
    const columns: Column<IPfLedgerEntry>[] = [
        { key: 'period', header: 'Period', priority: 1, helpKey: 'payroll.pfLedger.col.period', render: e => periodLabel(e.year, e.month) },
        { key: 'kind', header: 'Kind', priority: 1, helpKey: 'payroll.pfLedger.col.kind', render: e => humanizeEnum(e.kind) },
        { key: 'amount', header: 'Amount', priority: 1, align: 'right', helpKey: 'payroll.pfLedger.col.amount', render: e => money(e.amount) },
        { key: 'note', header: 'Note', priority: 3, helpKey: 'payroll.pfLedger.col.note', render: e => e.note || '—' },
    ];
    return <ResponsiveTable data-help-section="payroll.pfLedger.table" pageKey={pageKey} columns={columns} rows={entries} getRowKey={e => e.id} emptyMessage="No ledger entries yet." />;
};

// W3-J: provident-fund members, their balances and the per-member ledger.
const PfMembersTab: React.FC<Props> = ({ institutionId, canManage }) => {
    const { showSuccess, showApiError } = useToast();
    const [members, setMembers] = useState<IPfMember[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    // dialog: null closed; {member: undefined} = add; {member} = edit
    const [dialog, setDialog] = useState<{ member?: IPfMember } | null>(null);
    const [employee, setEmployee] = useState<EmployeeOption | null>(null);
    const [joinDate, setJoinDate] = useState('');
    const [openingDate, setOpeningDate] = useState('');
    const [openingEmployee, setOpeningEmployee] = useState('');
    const [openingEmployer, setOpeningEmployer] = useState('');
    const [openingProfit, setOpeningProfit] = useState('');
    const [status, setStatus] = useState<PfMemberStatus>('ACTIVE');
    const [leftDate, setLeftDate] = useState('');
    const [ledger, setLedger] = useState<IPfMemberLedger | null>(null);
    const [downloading, setDownloading] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) return;
        setLoading(true);
        try {
            setMembers(await ProvidentFundService.listMembers(institutionId));
        } catch (e) {
            showApiError(e, 'Failed to load provident fund members');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    useEffect(() => { load(); }, [load]);

    const openDialog = (member?: IPfMember) => {
        setDialog({ member });
        setEmployee(null);
        setJoinDate(member?.joinDate ?? toLocalIsoDate());
        setOpeningDate(member?.openingDate ?? '');
        setOpeningEmployee(member ? String(member.openingEmployee ?? 0) : '');
        setOpeningEmployer(member ? String(member.openingEmployer ?? 0) : '');
        setOpeningProfit(member ? String(member.openingProfit ?? 0) : '');
        setStatus(member?.status ?? 'ACTIVE');
        setLeftDate(member?.leftDate ?? '');
    };

    const editing = !!dialog?.member;
    const valid = !!joinDate && (editing || !!employee) && (status !== 'LEFT' || !!leftDate);

    const save = async () => {
        if (!dialog) return;
        setSaving(true);
        const common = {
            joinDate,
            openingDate: openingDate || null,
            openingEmployee: num(openingEmployee),
            openingEmployer: num(openingEmployer),
            openingProfit: num(openingProfit),
        };
        try {
            if (dialog.member) {
                await ProvidentFundService.updateMember(dialog.member.id, {
                    ...common, status, leftDate: status === 'LEFT' ? leftDate : null,
                });
            } else if (employee) {
                await ProvidentFundService.createMember(institutionId, { userId: employee.id, ...common });
            }
            showSuccess(dialog.member ? 'Member updated' : 'Member added');
            setDialog(null);
            await load();
        } catch (e) {
            showApiError(e, 'Failed to save the member');
        } finally {
            setSaving(false);
        }
    };

    const openLedger = async (id: string) => {
        try {
            setLedger(await ProvidentFundService.ledger(id));
        } catch (e) {
            showApiError(e, 'Failed to load the ledger');
        }
    };

    const download = async (id: string) => {
        setDownloading(true);
        try {
            await ProvidentFundService.downloadStatement(id);
        } catch (e) {
            showApiError(e, 'Failed to download the statement');
        } finally {
            setDownloading(false);
        }
    };

    const columns: Column<IPfMember>[] = [
        { key: 'emp', header: 'Employee', priority: 1, helpKey: 'payroll.pfMembers.col.emp', render: m => `${m.employeeName}${m.employeeCode ? ` (${m.employeeCode})` : ''}` },
        { key: 'join', header: 'Joined', priority: 3, helpKey: 'payroll.pfMembers.col.join', render: m => formatDate(m.joinDate) },
        { key: 'ee', header: 'Employee share', priority: 2, align: 'right', helpKey: 'payroll.pfMembers.col.ee', render: m => money(m.employeeBalance) },
        { key: 'er', header: 'Employer share', priority: 2, align: 'right', helpKey: 'payroll.pfMembers.col.er', render: m => money(m.employerBalance) },
        { key: 'pr', header: 'Profit', priority: 3, align: 'right', helpKey: 'payroll.pfMembers.col.pr', render: m => money(m.profitBalance) },
        { key: 'tot', header: 'Total balance', priority: 1, align: 'right', helpKey: 'payroll.pfMembers.col.tot', render: m => money(m.totalBalance) },
        { key: 'vest', header: 'Vested', priority: 2, align: 'right', helpKey: 'payroll.pfMembers.col.vest', render: m => `${m.vestedPercent}%` },
        { key: 'wd', header: 'Withdrawable', priority: 2, align: 'right', helpKey: 'payroll.pfMembers.col.wd', render: m => money(m.withdrawable) },
        {
            key: 'status', header: 'Status', priority: 1,
            helpKey: 'payroll.pfMembers.col.status', render: m => <Chip size="small" color={m.status === 'ACTIVE' ? 'success' : 'default'} label={humanizeEnum(m.status)} />,
        },
        {
            key: 'actions', header: 'Actions', helpKey: 'payroll.pfMembers.col.actions', role: 'actions', hideInCard: true,
            render: m => (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" data-help="payroll.pfMembers.action.ledger" onClick={() => openLedger(m.id)}>Ledger</Button>
                    {canManage && <Button size="small" data-help="payroll.pfMembers.action.edit" onClick={() => openDialog(m)}>Edit</Button>}
                </Box>
            ),
        },
    ];

    return (
        <Box data-help-page="payroll.pfMembers.page">
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }} data-help-section="payroll.pfMembers.toolbar">
                {canManage && (
                    <Button data-help="payroll.pfMembers.add" variant="contained" startIcon={<AddIcon />} onClick={() => openDialog()} disabled={!institutionId}>Add member</Button>
                )}
            </Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <ResponsiveTable data-help-section="payroll.pfMembers.table" pageKey="pf-members" columns={columns} rows={members} getRowKey={m => m.id}
                    emptyMessage="No provident fund members yet." />
            )}

            <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? `Edit member: ${dialog?.member?.employeeName}` : 'Add provident fund member'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {!editing && <EmployeeAutocomplete value={employee} onChange={setEmployee} required activeOnly data-testid="pf-employee" data-help="payroll.pfMembers.form.employee" />}
                        <TextField data-help="payroll.pfMembers.form.joinDate" label="Join date" type="date" value={joinDate} onChange={e => setJoinDate(e.target.value)} required fullWidth
                            InputLabelProps={{ shrink: true }} />
                        <TextField data-help="payroll.pfMembers.form.openingDate" label="Opening balance date" type="date" value={openingDate} onChange={e => setOpeningDate(e.target.value)} fullWidth
                            InputLabelProps={{ shrink: true }} helperText="Date the opening balances below were struck (optional)." />
                        <TextField data-help="payroll.pfMembers.form.openingEmployee" label="Opening employee share" type="number" value={openingEmployee} fullWidth
                            onChange={e => setOpeningEmployee(e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
                        <TextField data-help="payroll.pfMembers.form.openingEmployer" label="Opening employer share" type="number" value={openingEmployer} fullWidth
                            onChange={e => setOpeningEmployer(e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
                        <TextField data-help="payroll.pfMembers.form.openingProfit" label="Opening profit" type="number" value={openingProfit} fullWidth
                            onChange={e => setOpeningProfit(e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
                        {editing && (
                            <>
                                <SearchableSelect data-help="payroll.pfMembers.form.status" label="Status" options={STATUSES} value={status} clearable={false}
                                    onChange={id => id && setStatus(id as PfMemberStatus)} getOptionLabel={o => o.label} />
                                {status === 'LEFT' && (
                                    <TextField data-help="payroll.pfMembers.form.leftDate" label="Left date" type="date" value={leftDate} onChange={e => setLeftDate(e.target.value)} required fullWidth
                                        InputLabelProps={{ shrink: true }} />
                                )}
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(null)} disabled={saving}>Cancel</Button>
                    <Button data-help="payroll.pfMembers.form.save" variant="contained" onClick={save} disabled={!valid || saving}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!ledger} onClose={() => setLedger(null)} maxWidth="md" fullWidth>
                <DialogTitle>{ledger && `Ledger: ${ledger.member.employeeName}`}</DialogTitle>
                <DialogContent dividers>
                    {ledger && (
                        <>
                            <Typography variant="body2" sx={{ mb: 1 }} data-help="payroll.pfLedger.summary">
                                Total balance {money(ledger.member.totalBalance)} (employee {money(ledger.member.employeeBalance)},
                                employer {money(ledger.member.employerBalance)}, profit {money(ledger.member.profitBalance)}).
                                Vested {ledger.member.vestedPercent}%, withdrawable {money(ledger.member.withdrawable)}.
                            </Typography>
                            <LedgerTable entries={ledger.entries} pageKey="pf-member-ledger" />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="payroll.pfLedger.download" onClick={() => ledger && download(ledger.member.id)} disabled={downloading}>Download statement (PDF)</Button>
                    <Button onClick={() => setLedger(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PfMembersTab;
